import { useEffect } from 'react';
import { useInstitutionsStore } from '@/stores/institutions';
import { useMedwandStore } from '@/stores/medwand';
import { useSocketStore } from '@/stores/socket';
import { ESocketListenMessageType } from '@/types/socket';

/**
 * Keeps the MedWand vitals in sync with the backend: the firehose `vitals`
 * event carries the stored reading itself, so it is merged into the store
 * directly — no refetch — and an already loaded card picks up newly captured
 * readings without a manual refresh. Active only while MedWand is enabled for
 * the current institution.
 */
export const useVitalsGateway = () => {
  const { listenMessage, stopListenMessage, isConnected } = useSocketStore(
    (state) => ({
      listenMessage: state.listenMessage,
      stopListenMessage: state.stopListenMessage,
      isConnected: state.isConnected,
    }),
  );

  const isMedWandEnabled = useInstitutionsStore(
    (state) => !!state.currentInstitution?.configuration?.medwand,
  );

  const addVitals = useMedwandStore((state) => state.addVitals);

  useEffect(() => {
    if (!isConnected || !isMedWandEnabled) return;

    listenMessage({
      type: ESocketListenMessageType.VITALS,
      callback: ({ vitals }) => {
        addVitals(vitals);
      },
    });

    return () => {
      stopListenMessage(ESocketListenMessageType.VITALS);
    };
  }, [
    isConnected,
    isMedWandEnabled,
    listenMessage,
    stopListenMessage,
    addVitals,
  ]);
};
